import React, { createContext, useContext } from 'react';
import type { FC, ReactNode } from 'react';
import { match } from 'variant';
import { Spinner, useAsync } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useARMClient } from '@wix/arm-client';
import { useCustomElementsMigration } from './useCustomElementsMigration.js';
import { useProjectModel } from '../../model.js';
import { useBlocksComponentsMigration } from './useBlocksComponentsMigrations.js';

interface AppMigrationsContextProps {
  addedSitePresence: boolean;
}

const AppMigrationsContext = createContext<AppMigrationsContextProps | null>(
  null
);

export const AppComponentsMigrations: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { getAppByVersion } = useDevCenterClient();
  const { createComponents } = useARMClient();
  const getCustomElementMigrationComponents = useCustomElementsMigration();
  const getBlocksMigrationComponents = useBlocksComponentsMigration();
  const { t } = useTranslation();
  const { model } = useProjectModel();

  const appMigrationsContextData = useAsync(async () => {
    const {
      data: { components },
    } = await getAppByVersion({
      appId: model.config.appId,
    });

    const newBlocksComponents = getBlocksMigrationComponents(components) ?? [];
    const newCustomElementsComponents =
      getCustomElementMigrationComponents(components) ?? [];

    const componentsToCreate = [
      ...newCustomElementsComponents,
      ...newBlocksComponents,
    ];

    if (componentsToCreate.length) {
      await createComponents(
        model.config.appId,
        componentsToCreate,
        model.config.projectId
      );
    }

    return {
      addedSitePresence: !!newCustomElementsComponents.length,
    };
  }, [model.components]);

  return match(appMigrationsContextData.status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: { addedSitePresence } }) => {
      return (
        <AppMigrationsContext.Provider value={{ addedSitePresence }}>
          {children}
        </AppMigrationsContext.Provider>
      );
    },
  });
};

export function useAppComponentsMigrationsContext(): AppMigrationsContextProps {
  const context = useContext(AppMigrationsContext);

  if (context == null) {
    throw new Error(
      'useAppMigrationsContext() cannot be used without a <AppMigrationsProvider>'
    );
  }

  return context;
}
